import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';
import { TableCellDefUIDirective } from '../directives/table-cell-def.directive';
import { TableHeaderCellDefUIDirective } from '../directives/table-header-cell-def.directive';
import { TableUIDataSource } from '../entities/table-data-source.entity';

@Injectable()
export class TableConstructUIService<DataType> {
  set dataSource(dataSource: TableUIDataSource<DataType> | DataType[] | null) {
    if (dataSource) {
      this._dataSource = Array.isArray(dataSource)
        ? new TableUIDataSource<DataType>(dataSource)
        : dataSource;
      this._ready$.next(true);
    }
  }

  get dataSource(): TableUIDataSource<DataType> {
    return this._dataSource;
  }

  private _dataSource!: TableUIDataSource<DataType>;
  private readonly _cells = new Map<string, TableCellDefUIDirective<DataType> | null>();
  private _orderedCells = new Map<string, TableCellDefUIDirective<DataType> | null>();
  private readonly _headerCells = new Map<string, TableHeaderCellDefUIDirective | null>();
  private _ready$ = new BehaviorSubject<boolean>(false);
  private _registeredColumnsTemplates: string[] = [];

  public data$: Observable<DataType[]> = this._ready$.pipe(
    filter(Boolean),
    switchMap(() => this._dataSource.data$),
  );

  public updateColumnsOrder(columns: string[]): void {
    const orderedCells = new Map<string, TableCellDefUIDirective<DataType> | null>();
    columns.forEach((column: string) => {
      orderedCells.set(column, this._cells.get(column) ?? null);
    });
    this._orderedCells = orderedCells;
  }

  public addCell(columnName: string, cell: TableCellDefUIDirective<DataType> | undefined): void {
    if (cell) {
      this._cells.set(columnName, cell);
      this._orderedCells.set(columnName, cell);
    }
  }

  public getCell(columnName: string): TableCellDefUIDirective<DataType> | null {
    return this._orderedCells.get(columnName) ?? null;
  }

  public addHeaderCell(columnName: string, cell: TableHeaderCellDefUIDirective | undefined): void {
    if (cell) {
      this._headerCells.set(columnName, cell);
    }
  }

  public getHeaderCell(columnName: string): TableHeaderCellDefUIDirective | null {
    return this._headerCells.get(columnName) ?? null;
  }

  public registerColumnTemplate(column: string): void {
    this._registeredColumnsTemplates.push(column);
  }

  public getRegisteredTemplateColumns(): string[] {
    return this._registeredColumnsTemplates;
  }
}
